"use client";

import { cn } from "utils";
import { LottieLoading } from "@ospweb/sdk-ui";
import { InteractiveToolCardShell } from "./AskUserQuestionCard";
import type { AskUserQuestionToolInfo } from "./types";

export interface InteractiveToolResultCardProps {
  /** 卡片标题（题面 / 引导文案） */
  title: string;
  /** 查看态正文（已选答案 / 已执行操作） */
  body: string;
  /** 为 true 时在正文前展示 loading（如绑定后拉取持仓） */
  bodyLoading?: boolean;
  /** 与交互态一致，用于 data-tool-name / data-tool-call-id 埋点与调试 */
  toolInfo?: AskUserQuestionToolInfo;
  className?: string;
  testId?: string;
}

/**
 * 交互式工具查看态单卡（只读）。
 * ask_user_question、request_portfolio 等工具在 result / role:tool content 存在时共用。
 */
export const InteractiveToolResultCard: React.FC<
  InteractiveToolResultCardProps
> = ({
  title,
  body,
  bodyLoading = false,
  toolInfo,
  className,
  testId = "interactive-tool-result-card",
}) => {
  const bodyText = body.trim();

  if (!bodyText && !bodyLoading) {
    return null;
  }

  return (
    <div data-testid={testId}>
      <InteractiveToolCardShell
        title={title}
        toolInfo={toolInfo}
        className={cn("mb-3 pb-4", className)}
      >
        <div className="flex items-center gap-2">
          {bodyLoading ? (
            <LottieLoading style={{ width: 20, height: 20 }} />
          ) : null}
          {bodyText ? (
            <p className="body_m text-content_primary">{bodyText}</p>
          ) : null}
        </div>
      </InteractiveToolCardShell>
    </div>
  );
};

export interface InteractiveToolResultCardsProps {
  items: Array<{ key: string; title: string; body: string }>;
  toolInfo?: AskUserQuestionToolInfo;
  className?: string;
}

/** 多卡纵向容器（如 ask_user_question 多题查看态） */
export const InteractiveToolResultCards: React.FC<
  InteractiveToolResultCardsProps
> = ({ items, toolInfo, className }) => {
  if (items.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex w-full max-w-[360px] mt-[10px] flex-col rounded-[14px] px-4 bg-background_solid_neutral_primary",
        className,
      )}
      data-testid="interactive-tool-result-cards"
    >
      {items.map((item, index) => {
        return (
          <InteractiveToolResultCard
            key={item.key}
            title={item.title}
            body={item.body}
            toolInfo={toolInfo}
            className={cn("mb-0 rounded-none px-0 bg-transparent", {
              "border-b border-border_white_transparent10 px-0":
                index !== items?.length - 1,
            })}
          />
        );
      })}
    </div>
  );
};
